import PageTitle from '../PageTitle';
import { ClaimContext } from 'context/ClaimContext';
import React, { useContext, useEffect, useRef, useState } from 'react';
import FormInput from '../FormInput';
import Vehicle from 'components/sitePages/vehicle/Vehicle';
import { CommonValidation } from '../commonValidation/commonValidation';
import { useTranslation } from 'react-i18next';
import { RoutePaths, SelectOptionsYesNo } from 'Constants/Common';
import { SelectInputHelper } from '../selectInput/SelectInput';
import ToggleButton from '../toggleButton/ToggleButton';
import { useNavigate } from 'react-router-dom';
import ErrorSummary from '../ErrorSummary/ErrorSummary';
import { ClaimRequest, GapClaimRequest } from '../CommonTypes';
import { useAuth0 } from '@auth0/auth0-react';
import { ClaimApi } from 'api/ClaimApi';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { vehicleByPolicy } from 'state/selectors/vehicles';
import { setSelectedPolicy } from 'state/slices/vehicleSlice';

const GapClaimSummary = (): JSX.Element => {
    const dispatch = useAppDispatch();
    const scrollDivRef = useRef<null | HTMLDivElement>(null);
    useEffect(() => {
        scrollDivRef.current?.scrollIntoView();
    }, []);
    const navigate = useNavigate();
    const { productReference, gapClaim, setClaimRequest } =
        useContext(ClaimContext);
    const customer = useAppSelector((s) => s.customer);
    const { getAccessTokenSilently } = useAuth0();
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const vehicle = useAppSelector((state) =>
        vehicleByPolicy(state, productReference.reference)
    );
    const { t, i18n } = useTranslation();

    const countryCode = i18n.language.toLowerCase();
    const incidentDate = CommonValidation.getDate(
        gapClaim.dateOfIncident,
        countryCode
    );
    const settlementRecdDate = CommonValidation.getDate(
        gapClaim.settlementReceivedDate,
        countryCode
    );

    let acceptanceTermsText = t('gap_acceptance_text');
    if (gapClaim.fullCoverage == SelectOptionsYesNo.No) {
        acceptanceTermsText =
            acceptanceTermsText +
            '<br/>' +
            t('gap_acceptance_no_insurance_text');
    }

    const saveRepairRequest = async () => {
        const successCallback = (submitttedRequest: ClaimRequest) => {
            setClaimRequest(submitttedRequest);

            navigate(RoutePaths.ClaimNextSteps);
        };

        const failureCallback = (error) => {
            console.log(`Failed to register policy: ${error}`);

            navigate(RoutePaths.ConfirmationError);
        };

        const repairRequest: GapClaimRequest = {
            gapClaim: {
                ...gapClaim,
                settlementReceived:
                    gapClaim.settlementReceived ===
                    SelectInputHelper.NullValue()
                        ? null
                        : gapClaim.settlementReceived,
                settlementReceivedDate: gapClaim.settlementReceivedDate
                    ? CommonValidation.getJsonDate(
                          gapClaim.settlementReceivedDate
                      ).toISOString()
                    : null,
                dateOfIncident: gapClaim.dateOfIncident
                    ? CommonValidation.getJsonDate(
                          gapClaim.dateOfIncident
                      ).toISOString()
                    : null
            },
            customer: customer,
            vehicle: vehicle,
            policy: productReference
        };

        console.log(`Repair requests: ${JSON.stringify(repairRequest)}`);

        const token = await getAccessTokenSilently();

        new ClaimApi().saveGapClaim(
            repairRequest,
            token,
            successCallback,
            failureCallback
        );
    };

    const onBackButtonClick = (): void => {
        dispatch(setSelectedPolicy(''));
        navigate(RoutePaths.ClaimDetails);
    };
    const onSubmitButtonClick = async () => {
        if (acceptedTerms !== true) {
            setErrorMessage(t('claim_summarypage_acceptance_error'));
        } else {
            setErrorMessage('');
            await saveRepairRequest();
        }
    };

    return (
        <div>
            <div ref={scrollDivRef}></div>
            <div className='card page-content long-header'>
                <PageTitle title={t('claim_summarypage_title')} />
                <FormInput
                    id='summary-vehicle-forminput'
                    label={t('vehicle_details')}>
                    <div
                        id='vehicle-summary-container'
                        className='div-container-style'>
                        <section>
                            {vehicle && (
                                <Vehicle
                                    id={'vehicle-${vehicle.vrn}'}
                                    vehicle={vehicle}
                                />
                            )}
                        </section>
                    </div>
                </FormInput>
                <FormInput
                    id='summary-claim-details-forminput'
                    label={t('claim_details')}>
                    <div
                        id='claim-details-summary-container'
                        className='div-container-style'>
                        <section className='claim-details-info'>
                            <div>
                                {gapClaim.locationCountry != undefined &&
                                    gapClaim.locationCountry !=
                                        SelectInputHelper.NullValue() && (
                                        <div>
                                            <b>{t('claim_country')}</b>
                                            {gapClaim.locationCountry}
                                            <br />
                                        </div>
                                    )}
                                {incidentDate != undefined && (
                                    <div>
                                        <b>{t('claim_incident_date')}</b>
                                        {incidentDate}
                                        <br />
                                    </div>
                                )}
                                {gapClaim.vehicleStolen != undefined &&
                                    gapClaim.vehicleStolen !=
                                        SelectInputHelper.NullValue() && (
                                        <div>
                                            <b>{t('claim_theft')}</b>
                                            {gapClaim.vehicleStolen}
                                            <br />
                                        </div>
                                    )}
                                {gapClaim.outstandingLoanBalance != undefined &&
                                    gapClaim.outstandingLoanBalance !=
                                        SelectInputHelper.NullValue() && (
                                        <div>
                                            <b>
                                                {t(
                                                    'claim_outstanding_loan_balance'
                                                )}
                                            </b>
                                            {gapClaim.outstandingLoanBalance}
                                            <br />
                                        </div>
                                    )}
                                {gapClaim.fullCoverage != undefined && (
                                    <div>
                                        <b>{t('claim_insurance')}</b>
                                        {gapClaim.fullCoverage}
                                        <br />
                                    </div>
                                )}
                            </div>
                        </section>
                    </div>
                </FormInput>
                <FormInput
                    id='summary-insurance-forminput'
                    label={t('summary_insurance')}>
                    {gapClaim.fullCoverage == SelectOptionsYesNo.Yes && (
                        <div
                            id='insurance-details-summary-container'
                            className='div-container-style'>
                            <section className='insurance-details-info'>
                                <div>
                                    {gapClaim.settlementReceived !=
                                        undefined && (
                                        <div>
                                            <b>
                                                {t(
                                                    'summary_settlement_received'
                                                )}
                                            </b>
                                            {gapClaim.settlementReceived}
                                            <br />
                                        </div>
                                    )}

                                    {gapClaim.settlementReceivedDate !=
                                        undefined && (
                                        <div>
                                            <b>
                                                {t(
                                                    'summary_settlement_recd_date'
                                                )}
                                            </b>
                                            {settlementRecdDate}
                                            <br />
                                        </div>
                                    )}
                                </div>
                            </section>
                        </div>
                    )}
                    {gapClaim.fullCoverage == SelectOptionsYesNo.No && (
                        <div
                            className='alert alert-warning panel-selection-alert-container'
                            role='alert'>
                            {t('vehicle_appraisal_needed')}
                        </div>
                    )}
                </FormInput>
                <FormInput id='terms-form' errorText={errorMessage}>
                    <div className='col'>
                        <ToggleButton
                            value={acceptedTerms}
                            onChange={setAcceptedTerms}
                            label={acceptanceTermsText}
                        />
                    </div>
                </FormInput>
            </div>
            <div className='card-footer page-content-footer'>
                {errorMessage && (
                    <ErrorSummary
                        title='Summary errors'
                        errors={[errorMessage]}
                        className='summary-errors'
                    />
                )}
            </div>
            <div className='bottom-navigation'>
                <button
                    data-testid='primary-button'
                    className='btn btn-primary progress-button'
                    onClick={() => onSubmitButtonClick()}>
                    {t('submit_for_approval')}
                </button>
                <button
                    aria-label='back-button'
                    className='btn btn-secondary progress-button'
                    onClick={() => onBackButtonClick()}>
                    {t('back')}
                </button>
            </div>
        </div>
    );
};

export default GapClaimSummary;
